<template>
    <ion-page>
        <ion-header>
            <ion-toolbar color="primary">
                <ion-buttons slot="start">
                    <ion-back-button v-if="!loading" :text="$t('report_sheets.top_bar.back_to_steps')" :default-href="`/report-sheets/${sheet?.uuid}`"></ion-back-button>
                </ion-buttons>
                <ion-title>{{ $t('report_sheets.top_bar.title') }} : {{ sheet?.title ?? $t('report_sheets.top_bar.loading') }}</ion-title>
            </ion-toolbar>
        </ion-header>

        <div v-if="!loading" :class="$style['sub-header']">{{ $t('report_sheets.employees_page.sub_top_bar_title') }} ({{ sheet?.employees.length ?? 0 }})</div>

        <ion-content>
            <ContentSidebarWrapper side="left">
                <template v-slot:content>
                    <div v-if="loading">
                        <ReportSheetsSkeleton/>
                    </div>
                    <div v-else-if="sheet?.employees.length" style="padding-bottom: 200px;">
                        <ion-item v-for="employee in sheet?.employees" :key="employee.uuid" :button="true" @click="$router.push(`/report-sheets/${sheet?.uuid}/employees/${employee?.uuid}`)">
                            <div :class="$style['inner']">
                                <div>
                                    <h4>{{ employee?.firstname }} - {{ employee?.lastname }}</h4> 
                                    <p v-if="employee.total_cost">Total : {{ employee.total_cost }}€</p>
                                </div>
                            </div>
                        </ion-item>
                    </div>
                    <div v-else>
                        <ion-text class="ion-text-center" color="medium">
                            <p>{{ $t('report_sheets.employees_page.no_result') }}</p>
                        </ion-text>
                    </div>
                </template>

                <template v-slot:sidebar v-if="!loading && sheet?.total_employees_cost">
                    <p :class="$style['total_day']">{{ $t('report_sheets.employees_page.employee_total') }} : {{ sheet?.total_employees_cost ?? 0.00 }}€</p>
                </template>
            </ContentSidebarWrapper>

            <template v-if="!sheet?.employees_step_validated || userCanForceEdit()">
                <ion-fab slot="fixed" vertical="bottom" horizontal="end">
                    <ion-fab-button color="primary" @click="presentActionSheet">
                        <ion-icon :icon="caretUp"></ion-icon>
                    </ion-fab-button>
                </ion-fab>

                <UserSelectorButton style="display: none;" ref="userSelectorRef" @change="addUser($event.value)" :bypass-organization="true" />
            </template>
        </ion-content>
    </ion-page>
</template>

<script lang="ts">
    import {
        IonBackButton,
        IonButtons,
        IonHeader,
        IonPage,
        IonToolbar,
        IonContent,
        toastController,
        IonFab,
        IonFabButton,
        actionSheetController,
        alertController,
        IonIcon
    } from "@ionic/vue"

    import {
        caretUp
    } from "ionicons/icons"

    import { defineComponent } from "vue"
    import ReportSheetsSkeleton from "@/components/skeletons/ReportSheetsSkeleton.vue";
    import ContentSidebarWrapper from "@/components/wrappers/ContentSidebarWrapper.vue"
    import { ReportSheet } from "@/report-sheets"
    import UserSelectorButton from "@/components/data/selectors/UserSelectorButton.vue"
    import { User } from "@/users"
    import { useUserStore } from "@/stores/user"

    export default defineComponent({
        components: {
            ContentSidebarWrapper,
            ReportSheetsSkeleton,
            UserSelectorButton,
            IonBackButton,
            IonButtons,
            IonHeader,
            IonPage,
            IonToolbar,
            IonContent,
            IonFab,
            IonIcon,
            IonFabButton
        },
        setup() {
            const user = useUserStore()

            return { 
                caretUp,
                user 
            }
        },
        data() {
            return {
                loading: false as boolean | false,
                sheet: null as ReportSheet | null,
                fieldDisabled: true as boolean | true
            };
        },
        mounted() {
            if (this.user.is_logged_in)
                this.load()

            this.$watch("user.is_logged_in", (is_logged_in: boolean) => {
                if (is_logged_in) this.load();
            });
        },
        ionViewWillEnter() {
            if (this.user.is_logged_in)
                this.load()
        },
        methods: {
            load() {
                if (this.loading)
                    return

                this.loading = true
                
                this.axios.get(`/report-sheets/${this.$route.params.uuidReport}`)
                .then((responses) => {
                    this.sheet = responses.data

                    if (this.sheet && (!this.sheet.employees_step_validated || this.userCanForceEdit())) 
                        this.fieldDisabled = false

                    if (this.sheet) {
                        this.sheet.employees = this.sheet.employees.map((employee: any) => {
                            employee.employee_firstname = employee.employee_firstname ?? ''
                            employee.employee_lastname = employee.employee_lastname ?? ''

                            return employee
                        })
                    }
                }).catch((error) => {
                    console.error(error)
                }).finally(() => {
                    this.loading = false
                })
            },
            userCanForceEdit(): boolean {
                if (!this.sheet || !this.user) return false
                
                // Get user's role for this report sheet
                const userRole = this.sheet.roles?.find(role => role.user?.uuid === this.user.uuid)?.role
                
                if (!userRole) return false
                
                // Site-supervisor and manager can always modify, regardless of validation status
                if (['site-supervisor', 'manager'].includes(userRole)) {
                    return true
                }
                
                // Site-manager can modify if the report is not validated by site-manager or manager
                if (userRole === 'site-manager') {
                    return !this.sheet.validated_by_site_manager && !this.sheet.validated_by_manager
                }
                
                // Other roles can only modify if no validation has been done
                return !this.sheet.validated_by_site_supervisor && !this.sheet.validated_by_site_manager && !this.sheet.validated_by_manager
            },
            addUser(user: User) {
                this.loading = true

                this.axios.post(`/report-sheets/${this.$route.params.uuidReport}/employees`, {
                    employeeUuid: user.uuid
                }).then(async (response) => {
                    (
                        await toastController.create({
                            header: this.$t('report_sheets.employees_page.toast.employee.success'),
                            message: this.$t('report_sheets.employees_page.toast.employee.message'),
                            duration: 5000,
                            color: "success"
                        })
                    ).present()

                    this.sheet?.employees.push(response.data)
                    this.$router.push(`/report-sheets/${this.$route.params.uuidReport}/employees/${response.data.uuid}`)
                }).catch((error) => {
                    console.error(error)
                }).finally(() => {
                    this.loading = false
                })
            },
            stepValidation() {
                this.loading = true

                this.axios.put(`/report-sheets/${this.$route.params.uuidReport}/step-validation`, {
                    step: 'employees_step_validated'
                }).then(async () => {
                    (
                        await toastController.create({
                            header: this.$t('report_sheets.employees_page.toast.validation.success'),
                            message: this.$t('report_sheets.employees_page.toast.validation.message'),
                            duration: 5000,
                            color: "success"
                        })
                    ).present()

                    this.$router.push(`/report-sheets/${this.$route.params.uuidReport}`)
                }).catch((error) => {
                    console.error(error)
                    this.loading = false
                })
            },
            triggerUserSelector() {
                const userSelector = this.$refs.userSelectorRef as any

                if (userSelector?.openSelector)
                    userSelector.openSelector()
            },
            async presentActionSheet() {
                const actionSheet = await actionSheetController.create({
                    buttons: [
                        {
                            text: this.$t('report_sheets.employees_page.popup.add_employee'),
                            handler: () => {
                                this.triggerUserSelector()
                            }
                        },
                        {
                            text: this.$t('report_sheets.employees_page.popup.step_validation'),
                            handler: async () => {
                                const alert = await alertController.create({
                                    message: this.$t('report_sheets.employees_page.alert.title'),
                                    htmlAttributes: {
                                        'aria-label': 'alert dialog'
                                    },
                                    buttons: [
                                        {
                                            text: this.$t('report_sheets.employees_page.alert.btn_cancel'),
                                            role: "cancel"
                                        },
                                        {
                                            text: this.$t('report_sheets.employees_page.alert.btn_confirm'),
                                            role: "confirm",
                                            handler: () => {
                                                this.stepValidation()
                                            }
                                        }
                                    ]
                                })

                                await alert.present()
                            }
                        },
                        {
                            text: this.$t('report_sheets.employees_page.popup.btn_cancel'),
                            role: "cancel"
                        }
                    ]
                })

                await actionSheet.present()
            }
        }
    })
</script>

<style lang="scss" module>
    .sub-header {
        background: #000;
        color:white;
        height: 50px;
        width: 100%;
        display: grid;
        align-content: center;
        justify-content: center;
    }
    .inner {
        width: 100%;
        display: flex;
        flex-direction: row;
        align-items: flex-start;
        justify-content: space-between;
        padding: 1em;
        font-size: .9em;

        @media(max-width: 990px) {
            font-size: .8em;
        }
        @media(max-width: 700px) {
            flex-direction: column
        }
        h4, p {
            padding: 0;
            margin: 0;
        }
        p {
            margin-top: .25em;
            color: var(--ion-color-medium);
        }
        .right {
            display: flex;
            align-items: center;

            @media(min-width: 701px) {
                flex-direction: column;
                align-items: flex-end;
            }

            @media(max-width: 700px) {
                .date {
                    &:before {
                        content: '—';
                        margin: 0 .5em;
                        margin-top: 0;
                    }
                }
            }
        }
    }
    .total_day {
        color: #fff;
        background: #9e9e9e;
        margin: 0;
        padding: 10px;
        text-align: center;
    }
</style>